/* @flow */
import * as React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import Moment from 'react-moment';
import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';

import type TypeForm from 'forms/_.extend';

import SiteAddress from 'components/SiteAddress/SiteAddress';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroRadioGroup from 'forms/components/inputs/ElectroRadioGroup/ElectroRadioGroup';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';
import ActionDrawerTable from 'views/ActionDrawer/components/ActionDrawerTable/ActionDrawerTable';
import ActionDrawerFormButtons from 'views/ActionDrawer/components/ActionDrawerFormButtons/ActionDrawerFormButtons';

import styles from '../../../../ActionDrawer.module.scss';

export type InputValue = $Values<typeof InputValues>;
export const InputValues = Object.freeze({
  NEW_LOCATION: 'NEW_LOCATION',
  CURRENT_LOCATION: 'CURRENT_LOCATION',
  CONTRACT_LOCATION: 'CONTRACT_LOCATION',
  USER_CONTACT_DETAILS: 'USER_CONTACT_DETAILS',
  CONTRACT_PICKUP_DETAILS: 'CONTRACT_PICKUP_DETAILS',
  NEW_CONTACT_DETAILS: 'NEW_CONTACT_DETAILS',
});

type TypeProps = {
  t: TranslatorProps,
  asset: Object,
  form: TypeForm,
};

@translate()
@observer
export default class ReportAssetFaultForm extends React.Component<*> {
  @observable newLocation = false;
  @observable newContactDetails = false;

  onInputChange(value: InputValue) {
    if (
      value === InputValues.NEW_LOCATION ||
      value === InputValues.CURRENT_LOCATION ||
      value === InputValues.CONTRACT_LOCATION
    ) {
      this.newLocation = value === InputValues.NEW_LOCATION;
    }
    if (
      value === InputValues.USER_CONTACT_DETAILS ||
      value === InputValues.CONTRACT_PICKUP_DETAILS ||
      value === InputValues.NEW_CONTACT_DETAILS
    ) {
      this.newContactDetails = value === InputValues.NEW_CONTACT_DETAILS;
    }
  }

  render() {
    const { t, asset, form }: TypeProps = this.props;

    let details = [
      [t('general:assets.labels.number'), asset.asset_number],
      [t('general:manufacturer'), asset.manufacturer],
      [t('general:model'), asset.model],
      [t('general:labels.serial_number'), asset.serial_number],
    ];

    if (!asset.owned) {
      details = [
        [t('general:trading_account'), asset.contract.company_name],
        [t('general:contracts.labels.number'), asset.contract.er_contract_number],
      ].concat(details);
    }

    return (
      <React.Fragment>
        <div className={styles['action-drawer-content']}>
          <ActionDrawerBlock
            divider={true}
            gutters={true}
            heading={t('general:assets.labels.details')}
          >
            <ActionDrawerTable data={details} />
            {!asset.owned && (
              <div className={styles['info-block']}>
                <h5>{t('general:labels.hire_period')}:</h5>
                <ul>
                  <li>
                    <Moment date={asset.er_hire_start_date} format="MMMM DD, YYYY" />{' '}
                    {t('general:to')}{' '}
                    <Moment date={asset.er_hire_end_date} format="MMMM DD, YYYY" />
                  </li>
                </ul>
              </div>
            )}
            <div className={styles['info-block']}>
              <h3>{t('general:assets.labels.current_location')}:</h3>
              {asset.location ? (
                <SiteAddress {...asset.location.site.address} />
              ) : (
                <ul>
                  <li>{t('general:search_results.no_location_found')}</li>
                </ul>
              )}
            </div>
          </ActionDrawerBlock>
          <ActionDrawerBlock
            divider={true}
            gutters={true}
            heading={form.$('collectionDetails').label}
          >
            <ElectroDatePicker
              field={form.$('collectionDetails.collectionDate')}
              minDate={form.$('collectionDetails.collectionDate').value}
            />
            <ElectroTextField field={form.$('collectionDetails.collectionPhoneNumber')} />
            {!asset.owned && (
              <React.Fragment>
                <ElectroRadioGroup
                  field={form.$('collectionDetails.collectLocation')}
                  onChange={this.onInputChange.bind(this)}
                />
                {this.newLocation && (
                  <ElectroTextField field={form.$('collectionDetails.newLocation')} />
                )}
                <ElectroRadioGroup
                  field={form.$('collectionDetails.contactDetails')}
                  onChange={this.onInputChange.bind(this)}
                />
                {this.newContactDetails && (
                  <React.Fragment>
                    <ElectroTextField field={form.$('collectionDetails.newContactDetailsName')} />
                    <ElectroTextField field={form.$('collectionDetails.newContactDetailsEmail')} />
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </ActionDrawerBlock>
          <ActionDrawerBlock gutters={true} heading={form.$('additionalInformation').label}>
            <div className={styles['form-group']}>
              <ElectroTextField
                field={form.$('additionalInformation.faultDetails')}
                props={{
                  multiline: true,
                  rows: 3,
                }}
              />
            </div>
            <div className={styles['form-group']}>
              <ElectroCheckbox field={form.$('additionalInformation.replacementRequired')} />
              <ElectroCheckbox field={form.$('additionalInformation.keepOriginalPackaging')} />
            </div>
            {!form.$('additionalInformation.keepOriginalPackaging').value && (
              <div className={styles['form-group']}>
                <ElectroTextField
                  field={form.$('additionalInformation.packagingDescription')}
                  props={{
                    multiline: true,
                    rows: 3,
                  }}
                />
              </div>
            )}
          </ActionDrawerBlock>
        </div>
        <ActionDrawerFormButtons form={form} submitTxt={t('general:actions.report_fault')} />
      </React.Fragment>
    );
  }
}
